import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { Formik } from "formik";
import { AppDispatch, AppState } from "../../../redux/store";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../redux/features/common-slice";
import paths from "../../../navigation/paths";
import {
  allIncidentUsersStateAction,
  clearIncidentStates,
  createIncidentStateAction,
  getAllIncidentUsersState,
  IncidentRequest,
  Status,
  UrgencyLevel,
} from "../../../redux/features/incident-slice";
import Input from "../../../components/input";
import StyledSelect from "../../../components/styled-select";
import { MultiValue, SingleValue } from "react-select";
import { OptionValue } from "../../../types/component";
import Button from "../../../components/button";
import ToastComponent from "../../../components/toast";
import { isSelectMultiValue, isSelectSingleValue } from "../../../core/utils";
import {
  CommonStatus,
  getTechnicalUnitsState,
  technicalUnitsStateAction,
} from "../../../redux/features/other-slice";
import {
  allCustomersStateAction,
  CustomerStatus,
  getAllCustomersState,
} from "../../../redux/features/customer-slice";
import {
  clearUploadDocumentState,
  uploadDocumentStateAction,
} from "../../../redux/features/document-slice";

export default function CreateIncidentPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const errorState = useSelector(getErrorState);
  const createIncidentState = useSelector(
    (state: AppState) => state.incidents.createIncidentState
  );
  const customersState = useSelector(getAllCustomersState);
  const technicalUnitsState = useSelector(getTechnicalUnitsState);
  const usersState = useSelector(getAllIncidentUsersState);

  const [attachments, setAttachments] = useState<File[]>([]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      setAttachments(files);
    }
  };

  const handleSuccessToast = useCallback(
    (message: string) => {
      toast(message, { type: "success" });
      setTimeout(() => {
        navigate(paths.INCIDENTS, { replace: true });
      }, 2000);
    },
    [navigate]
  );

  useEffect(() => {
    dispatch(
      setBreadcrumbState([
        { id: paths.INCIDENTS, path: null, title: "Incidents" },
        { id: "createIncident", path: null, title: "Create Incident" },
      ])
    );

    // Fetch customers and technical units
    dispatch(allCustomersStateAction({ status: [CustomerStatus.Active] }));
    dispatch(technicalUnitsStateAction());
  }, [dispatch]);

  useEffect(() => {
    if (createIncidentState.data && createIncidentState.data.success) {
      if (attachments && attachments.length > 0) {
        attachments.forEach((attachment) => {
          dispatch(
            uploadDocumentStateAction({
              file: attachment,
              path: "incidents",
              incidentId: createIncidentState.data?.data?.id,
              ownerId: "",
            })
          );
        });
      }
      if (!errorState) {
        handleSuccessToast("Incident created");
        dispatch(clearIncidentStates());
        dispatch(clearUploadDocumentState());
      }
    }
  }, [createIncidentState, handleSuccessToast]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  const fetchAllUsers = useCallback(async () => {
    await dispatch(allIncidentUsersStateAction({ teamIds: [], roles: [] }));
  }, [dispatch]);

  useEffect(() => {
    fetchAllUsers();
  }, [fetchAllUsers]);

  return (
    <>
      <Helmet title="Create Incident" />
      <Formik
        initialValues={
          {
            title: "",
            urgencyLevel: UrgencyLevel.Medium,
            technicalUnitIds: [],
            customerId: "",
            description: "",
            actionPlan: "",
            followUpDate: new Date(),
            status: Status.Pending,
            occurrenceDate: new Date(),
            supervisorIds: [],
            memberIds: [],
          } as IncidentRequest
        }
        validationSchema={Yup.object().shape({
          title: Yup.string().required("Title is required"),
          urgencyLevel: Yup.string().required("Urgency Level is required"),
          technicalUnitIds: Yup.array().min(
            1,
            "At least one technical unit is required"
          ),
          customerId: Yup.string().required("Customer is required"),
          description: Yup.string().required("Description is required"),
          followUpDate: Yup.date().required("Follow-up Date is required"),
          occurrenceDate: Yup.date().required("Occurrence Date is required"),
          status: Yup.string().required("Status Level is required"),
          supervisorIds: Yup.array().min(
            1,
            "At least one supervisor is required"
          ),
          memberIds: Yup.array().min(1, "At least one member is required"),
        })}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(false);
          // Create a new object with the original Date for followUpDate
          const incidentData: IncidentRequest = {
            ...values,
            followUpDate: values.followUpDate, // Keep it as a Date object
            occurrenceDate: values.occurrenceDate,
          };
          dispatch(createIncidentStateAction({ req: incidentData }));
        }}
      >
        {({
          values,
          errors,
          handleChange,
          handleSubmit,
          isSubmitting,
          setValues,
        }) => (
          <form
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit}
            className="h-full overflow-y-auto"
          >
            <div
              className="h-full grid grid-cols-2 gap-8 overflow-y-scroll"
              style={{ height: "calc(100% - 56px)" }}
            >
              <div className="space-y-4">
                <Input
                  label="Title *"
                  type="text"
                  id="title"
                  value={values.title}
                  onChange={handleChange}
                  errorText={errors.title}
                />
                <div className="flex flex-col">
                  <label className="text-xs text-text-title">
                    Urgency Level *
                  </label>
                  <StyledSelect
                    options={Object.values(UrgencyLevel).map((level) => ({
                      value: level,
                      label: level,
                    }))}
                    isClearable={false}
                    menuPosition="fixed"
                    value={{
                      value: values.urgencyLevel,
                      label: values.urgencyLevel,
                    }}
                    onChange={(
                      value: SingleValue<OptionValue> | MultiValue<OptionValue>
                    ) => {
                      if (value && isSelectSingleValue(value)) {
                        setValues({
                          ...values,
                          urgencyLevel: value.value as UrgencyLevel,
                        });
                      }
                    }}
                  />
                  {errors.urgencyLevel && (
                    <p className="text-error-main text-xs">
                      {errors.urgencyLevel}
                    </p>
                  )}
                </div>
                <div className="flex flex-col">
                  <label className="text-xs text-text-title">
                    Technical Unit *
                  </label>
                  <StyledSelect
                    isCloseMenuOnSelect={false}
                    loading={technicalUnitsState.loading}
                    options={technicalUnitsState.data.map((e) => ({
                      value: e.id,
                      label: `${e.name}`,
                    }))}
                    isClearable={false}
                    multiple={true}
                    menuPosition="fixed"
                    value={
                      values.technicalUnitIds
                        ? (values.technicalUnitIds
                            .map((technicalUnitId) => {
                              const technicalUnit =
                                technicalUnitsState.data.find(
                                  (e) => e.id === technicalUnitId
                                );
                              return technicalUnit
                                ? {
                                    value: technicalUnit.id,
                                    label: `${technicalUnit.name}`,
                                  }
                                : null;
                            })
                            .filter(Boolean) as OptionValue[])
                        : []
                    }
                    onChange={(
                      value: SingleValue<OptionValue> | MultiValue<OptionValue>
                    ) => {
                      if (value && isSelectMultiValue(value)) {
                        const selectedtechnicalUnitIds = value.map(
                          (v) => v.value as string
                        );
                        setValues({
                          ...values,
                          technicalUnitIds: selectedtechnicalUnitIds,
                        });
                      } else {
                        setValues({
                          ...values,
                          technicalUnitIds: [],
                        });
                      }
                    }}
                  />
                  {errors.technicalUnitIds && (
                    <p className="text-error-main text-xs">
                      {errors.technicalUnitIds}
                    </p>
                  )}
                </div>
                <div className="flex flex-col">
                  <label className="text-xs text-text-title">Customer *</label>
                  <StyledSelect
                    loading={customersState.loading}
                    options={customersState.data.map((customer) => ({
                      value: customer.id,
                      label: customer.name,
                    }))}
                    isClearable={false}
                    menuPosition="fixed"
                    value={
                      values.customerId
                        ? {
                            value: values.customerId,
                            label:
                              customersState.data.find(
                                (customer) => customer.id === values.customerId
                              )?.name || "Select...",
                          }
                        : { value: "", label: "Select..." }
                    }
                    onChange={(
                      value: SingleValue<OptionValue> | MultiValue<OptionValue>
                    ) => {
                      if (value && isSelectSingleValue(value)) {
                        setValues({
                          ...values,
                          customerId: value.value as string,
                        });
                      }
                    }}
                  />
                  {errors.customerId && (
                    <p className="text-error-main text-xs">
                      {errors.customerId}
                    </p>
                  )}
                </div>
                <div className="flex flex-col">
                  <label className="text-xs text-text-title">Members *</label>
                  <StyledSelect
                    isCloseMenuOnSelect={false}
                    loading={usersState.loading}
                    options={usersState.data.map((e) => ({
                      value: e.id,
                      label: `${e.name} - ${e.email}`,
                    }))}
                    isClearable={false}
                    multiple={true}
                    menuPosition="fixed"
                    value={
                      values.memberIds
                        ? (values.memberIds
                            .map((memberId) => {
                              const user = usersState.data.find(
                                (e) => e.id === memberId
                              );
                              return user
                                ? {
                                    value: user.id,
                                    label: `${user.name}`,
                                  }
                                : null;
                            })
                            .filter(Boolean) as OptionValue[])
                        : []
                    }
                    onChange={(
                      value: SingleValue<OptionValue> | MultiValue<OptionValue>
                    ) => {
                      if (value && isSelectMultiValue(value)) {
                        const selectedmemberIds = value.map(
                          (v) => v.value as string
                        );
                        setValues({
                          ...values,
                          memberIds: selectedmemberIds,
                        });
                      } else {
                        setValues({
                          ...values,
                          memberIds: [],
                        });
                      }
                    }}
                  />
                  {errors.memberIds && (
                    <p className="text-error-main text-xs">
                      {errors.memberIds}
                    </p>
                  )}
                </div>
                <div className="flex flex-col">
                  <label className="text-xs text-text-title">
                    Supervisors *
                  </label>
                  <StyledSelect
                    isCloseMenuOnSelect={false}
                    loading={usersState.loading}
                    options={usersState.data.map((e) => ({
                      value: e.id,
                      label: `${e.name} - ${e.email}`,
                    }))}
                    isClearable={false}
                    multiple={true}
                    menuPosition="fixed"
                    value={
                      values.supervisorIds
                        ? (values.supervisorIds
                            .map((supervisorId) => {
                              const user = usersState.data.find(
                                (e) => e.id === supervisorId
                              );
                              return user
                                ? {
                                    value: user.id,
                                    label: `${user.name}`,
                                  }
                                : null;
                            })
                            .filter(Boolean) as OptionValue[])
                        : []
                    }
                    onChange={(
                      value: SingleValue<OptionValue> | MultiValue<OptionValue>
                    ) => {
                      if (value && isSelectMultiValue(value)) {
                        const selectedsupervisorIds = value.map(
                          (v) => v.value as string
                        );
                        setValues({
                          ...values,
                          supervisorIds: selectedsupervisorIds,
                        });
                      } else {
                        setValues({
                          ...values,
                          supervisorIds: [],
                        });
                      }
                    }}
                  />
                  {errors.supervisorIds && (
                    <p className="text-error-main text-xs">
                      {errors.supervisorIds}
                    </p>
                  )}
                </div>
                <Input
                  label="Description *"
                  type="text"
                  inputType="textarea"
                  id="description"
                  value={values.description}
                  onChange={handleChange}
                  errorText={errors.description}
                />
                <Input
                  label="Action Plan"
                  type="text"
                  id="actionPlan"
                  inputType="textarea"
                  value={values.actionPlan}
                  onChange={handleChange}
                  errorText={errors.actionPlan}
                />
                <div className="flex flex-col">
                  <label className="text-xs text-text-title">Status *</label>
                  <StyledSelect
                    options={Object.values(Status).map((level) => ({
                      value: level,
                      label: level,
                    }))}
                    isClearable={false}
                    menuPosition="fixed"
                    value={{
                      value: values.status,
                      label: values.status,
                    }}
                    onChange={(
                      value: SingleValue<OptionValue> | MultiValue<OptionValue>
                    ) => {
                      if (value && isSelectSingleValue(value)) {
                        setValues({
                          ...values,
                          status: value.value as Status,
                        });
                      }
                    }}
                  />
                </div>
                {errors.status && (
                  <p className="text-error-main text-xs">{errors.status}</p>
                )}
                <Input
                  label="Follow-up Date *"
                  type="date"
                  id="followUpDate"
                  value={values?.followUpDate?.toISOString().split("T")[0]}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      followUpDate: new Date(e.target.value),
                    })
                  }
                  errorText={errors.followUpDate}
                />
                <Input
                  label="Occurence Date *"
                  type="date"
                  id="occurenceDate"
                  value={values?.occurrenceDate?.toISOString().split("T")[0]}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      occurrenceDate: new Date(e.target.value),
                    })
                  }
                  errorText={errors.occurrenceDate}
                />
              </div>
              <div className="space-y-4">
                <div className="flex flex-col bg-background-default p-4 rounded-lg shadow">
                  <label className="text-xs text-text-title">Attachments</label>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="mt-2 p-2 border border-divider rounded-lg"
                    accept=".docx, .pdf, image/*"
                  />
                  {attachments.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm">Selected Files:</p>
                      <ul className="list-disc pl-5">
                        {attachments.map((file, index) => (
                          <li key={index} className="text-sm">
                            {file.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4 pt-4">
              <Button
                label="Submit"
                type="submit"
                loading={createIncidentState.loading}
                disabled={
                  isSubmitting || Object.values(errors).toString() !== ""
                }
              />
            </div>
          </form>
        )}
      </Formik>
      <ToastComponent id="createIncident" />
    </>
  );
}
